import React, { FC, useEffect, useState } from "react";
import { ExpressCodeGooglePassData } from "../../types/ExpressCodeGooglePassData";
import passImage from "./images/Add_to_Google_Wallet_button.svg";
import $ExpressCodeGooglePassButton from "./styles";
import logger from "../../utility/logger/logger";
import { SVG } from "../svg";
import ga from "../../utility/GAEmitter";
import {
  AnalyticCategories,
  ReturnStartedAndReturnStatusPageActions,
  WalletPassSource,
  WalletPassType,
  walletPassEventPayload,
} from "../../types/Analytics";
import { DataCyStrings } from "../../types/DataCyStrings";
import { CONTEXT_TOKEN_PARAM } from "../../globalConstants";

const ExpressCodeGooglePassButton: FC<ExpressCodeGooglePassData> = ({
  returnID,
  source = WalletPassSource.ReturnStatusPage,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loggedRender, setLoggedRender] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const analyticsPayload = walletPassEventPayload(returnID, WalletPassType.Google, source);

  // HOOKS
  useEffect(() => {
    if (!loggedRender) {
      ga.event({
        category: AnalyticCategories.ReturnStatusPage,
        action: ReturnStartedAndReturnStatusPageActions.AddToGoogleWalletButtonLoad,
        payload: analyticsPayload,
      });
      setLoggedRender(true);
    }
  }, [loggedRender]);

  const generateGooglePass = () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setErrorMessage("");
    const popupError = "Unable to open new tab. Please check your browser's popup settings and try again."

    try {
      const queryString = window.location.search;
      const params = new URLSearchParams(queryString);
      const authorization = params.get("authorization");
      const contextToken = params.get(CONTEXT_TOKEN_PARAM);

      // Build URL with returnID as path parameter and authorization as query parameter
      const urlParams = new URLSearchParams({ source });

      // Add authorization to URL parameters to avoid CORS issues
      if (authorization) {
        urlParams.append("authorization", authorization);
      }

      if (contextToken) {
        urlParams.append(CONTEXT_TOKEN_PARAM, contextToken);
      }

      const googlePassUrl = `/googlepassurl/${returnID}?${urlParams.toString()}`;

      // Track click event BEFORE window.open to ensure it's captured
      // even if popup is blocked or navigation fails
      ga.event({
        category: AnalyticCategories.ReturnStatusPage,
        action: ReturnStartedAndReturnStatusPageActions.AddToGoogleWalletButtonClick,
        payload: analyticsPayload,
      });

      // Navigate directly to the Google Pass endpoint and let the browser handle the redirect
      // This avoids CORS issues because:
      // 1. We navigate to our own endpoint (same origin)
      // 2. Our server responds with 302 redirect to Google Wallet
      // 3. Browser follows redirect naturally (no CORS)
      // 
      // Mobile considerations:
      // - Opens in new tab to preserve return status page
      // - The redirect to Google Wallet URL will trigger native app on Android
      // - Must be synchronous (no await) to avoid popup blockers
      const newTab = window.open(googlePassUrl, "_blank");
      
      if (!newTab) {
        // Popup was blocked - track failure with specific reason
        ga.event({
          category: AnalyticCategories.ReturnStatusPage,
          action: ReturnStartedAndReturnStatusPageActions.AddToGoogleWalletButtonFailure,
          label: "popup_blocked",
          payload: analyticsPayload,
        });
        throw new Error(popupError);
      }
      
      // Successfully opened - user will be redirected to Google Wallet
      ga.event({
        category: AnalyticCategories.ReturnStatusPage,
        action: ReturnStartedAndReturnStatusPageActions.AddToGoogleWalletOpenedNewTab,
        payload: analyticsPayload,
      });
      setIsLoading(false);
    } catch (error: any) {
      logger.Error("error generating google pass: " + error.toString());
      
      // Determine failure reason for tracking
      let failureReason = "unknown_error";
      let errorMessage = "Unable to open Google Wallet pass. Please try again.";
      
      if (error.message.includes("popup")) {
        failureReason = "popup_blocked";
        errorMessage = popupError;
      } else if (error.message) {
        // Sanitize error message for GA label (remove special chars, limit length)
        failureReason = error.message.toLowerCase().replace(/[^a-z0-9_]/g, '_').substring(0, 50);
      }
      
      setErrorMessage(errorMessage);

      // Track failure event (only if not already tracked for popup)
      if (!error.message.includes("popup")) {
        ga.event({
          category: AnalyticCategories.ReturnStatusPage,
          action: ReturnStartedAndReturnStatusPageActions.AddToGoogleWalletButtonFailure,
          label: failureReason,
          payload: analyticsPayload,
        });
      }
      
      setIsLoading(false);
    }
  };

  return (
    <$ExpressCodeGooglePassButton>
      <>
        <div className="google-pass-button-container" data-cy={DataCyStrings.nextStepsGoogleWalletButton}>
          <input
            type="image"
            className={`google-pass-image ${isLoading ? 'loading' : ''}`}
            src={passImage}
            onClick={generateGooglePass}
            disabled={isLoading}
            alt="Add your pass to the Google Wallet app where you can store and access the pass."
            aria-label="Add to Google Wallet"
          />
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner" />
            </div>
          )}
        </div>
        {errorMessage && (
          <div className="error-banner" role="alert">
            <div className="icon">
              <SVG name="exclamation" />
            </div>
            <div className="error-text">{errorMessage}</div>
          </div>
        )}
      </>
    </$ExpressCodeGooglePassButton>
  );
};

export default ExpressCodeGooglePassButton;
